import cloneDeep from 'lodash-es/cloneDeep'
import noop from 'lodash-es/noop'
import { hasSubCategories, setLayerManifest, setLayerPriorityMap, setMapLayers, updateMapLayers, updateMapLayersOpacity } from '../lib/layers'
import { DEFAULT_LAYER_OPACITY, MAP_LAYER_IDS, MAX_ACTIVE_LAYERS, MAX_RECENT_LAYERS } from '../lib/constants'
import { getAllLayers } from '../services/layers'
import { saveCategoryOpacity, saveFavoriteStates, saveLayers } from '../lib/userPreferences'
import FeatureLayer from '@arcgis/core/layers/FeatureLayer'

export default {
  namespace: 'layers',
  state: {
    allLayersLoading: true,
    layerPriorityMap: {},
    layersAtLocationData: {},
    layersByEvent: [],
    layersByState: [],
    layersError: null,
    layersVersion: null,
    overlaysAtLocationData: {},
    overlays: [],
    preferences: {
      active: [],
      category: {
        opacity: []
      },
      favorites: {
        states: []
      },
      recent: []
    },
    selected: {
      categories: [],
      categoryIsOpen: false,
      categoryName: null,
      stateName: null
    }
  },
  effects: {
    *enableOverlay({ payload: overlay }, { select, put }) {
      const { mapRef } = yield select(model => model.map)
      if (mapRef != null) {
        const contourContentLayerIndex = mapRef.layers.items.findIndex(i => i.id === MAP_LAYER_IDS.contour)
        const overlayLayer = mapRef.findLayerById(overlay.id)
        if (!overlayLayer) {
          const newOverlayLayer = new FeatureLayer({
            url: overlay.url,
            id: overlay.id,
            refreshInterval: overlay.refreshInterval
          })
          newOverlayLayer.priority = overlay.priority
          let insertIndex = contourContentLayerIndex + 1
          const mapLayerItems = mapRef.layers.items
          for (let li = insertIndex; li < mapLayerItems.length; li++) {
            if (mapLayerItems[li]?.id.includes(MAP_LAYER_IDS.overlay)) {
              if (newOverlayLayer.priority < mapLayerItems[li].priority) {
                insertIndex = li + 1
              }
            } else {
              break
            }
          }
          mapRef.layers.addMany([newOverlayLayer], insertIndex)
          yield put({
            type: 'setOverlayEnabled',
            payload: {
              id: overlay.id,
              enabled: true
            }
          })
        }
      }
    },
    *disableOverlay({ payload: overlay }, { select, put }) {
      const overlayId = overlay.id
      const { mapRef } = yield select(model => model.map)
      if (mapRef != null) {
        const layer = mapRef.findLayerById(overlayId)
        if (layer) {
          mapRef.layers.remove(layer)
          yield put({
            type: 'setOverlayEnabled',
            payload: {
              id: overlay.id,
              enabled: false
            }
          })
        }
      }
    },
    *getLayersListEffect({ callback = noop }, { call, put, select }) {
      const { mapRef } = yield select(model => model.map)
      const state = yield select(model => model.layers)
      const layerRequest = yield call(getAllLayers, null)
      if (layerRequest.success) {
        const manifestData = setLayerManifest(layerRequest.data, state.preferences)
        const priorityMap = setLayerPriorityMap(manifestData)
        const layers = setMapLayers({ ...manifestData, mapRef, priorityMap })
        if (layers.errored.length > 0) {
          yield put({
            type: 'setLayersState',
            payload: { enabled: false, keys: layers.errored }
          })
        }
        yield put({
          type: 'setLayersVersion',
          payload: manifestData.layerManifestVersion
        })
        yield put({
          type: 'setLayerPriorityMap',
          payload: priorityMap
        })
        yield put({
          type: 'setLayersData',
          payload: {
            eventLayers: manifestData.events,
            stateLayers: manifestData.states
          }
        })
        yield put({ type: 'setLayersLoading', payload: { status: false, type: 'all' } })
      }
      callback()
    },
    *setLayerCategoryOpacity({ payload }, { select, put }) {
      const { layersByEvent, layersByState } = yield select(model => model.layers)
      const { mapRef } = yield select(model => model.map)
      const { categoryName, opacity, stateName } = payload
      const allLayers = [...layersByEvent, ...layersByState]
      const { categories = [] } = allLayers.find(l => l.stateName == stateName)
      const category = categories.find(c => c.categoryName == categoryName)
      const layerIds = []
      if (hasSubCategories(category)) {
        for (let subCategory of category.subCategories) {
          for (let layer of subCategory.layers) {
            layerIds.push(`${stateName}-${categoryName}-${subCategory.subCategoryName}-${layer.layerName}`)
          }
        }
      }
      if (layerIds.length > 0) {
        updateMapLayersOpacity({ layerIds, opacity, mapRef })
        yield put({
          type: 'setLayersOpacity',
          payload
        })
      }
    },
    *setLayerEnabledStatus({ payload }, { select, put }) {
      const { layerPriorityMap: priorityMap, layersByEvent, layersByState } = yield select(model => model.layers)
      const { mapRef } = yield select(model => model.map)
      const { stateName, categoryName, subCategoryName, layerName, status } = payload
      const allLayers = [...layersByEvent, ...layersByState]
      const { categories } = allLayers.find(layer => layer.stateName == stateName)
      let categoryInfo = categories.find(category => category.categoryName == categoryName)
      if (subCategoryName != null && hasSubCategories(categoryInfo)) {
        categoryInfo = categoryInfo.subCategories.find(subCat => subCat.subCategoryName == subCategoryName)
      }
      const layers = categoryInfo.layers || []
      const currentLayer = layers.find(l => l.layerName === layerName)

      if (currentLayer) {
        currentLayer.control = {
          enabled: status
        }
        const updatedLayerKeys = updateMapLayers({
          layers: [
            {
              ...currentLayer,
              categoryName,
              stateName,
              subCategoryName
            }
          ],
          priorityMap,
          mapRef
        })
        yield put({
          type: 'setLayersByType',
          payload: {
            eventLayers: cloneDeep(layersByEvent),
            stateLayers: cloneDeep(layersByState)
          }
        })
        if (updatedLayerKeys.length > 0) {
          yield put({
            type: 'setLayersState',
            payload: { enabled: currentLayer.control.enabled, keys: updatedLayerKeys }
          })
        }
      }
    },
    *setMultipleLayersEnabledStatus({ payload }, { select, put }) {
      const { layerPriorityMap: priorityMap, layersByEvent, layersByState } = yield select(model => model.layers)
      const { mapRef } = yield select(model => model.map)
      const { all, status, toggledLayers } = payload
      const allLayers = [...layersByEvent, ...layersByState]
      const compositeKeys = []
      const layers = []

      toggledLayers.forEach(toggledLayer => {
        const { stateName, categoryName, subCategoryName, layerName, status } = toggledLayer
        const stateInfo = allLayers.find(al => al.stateName == stateName)
        const category = stateInfo.categories.find(c => c.categoryName == categoryName)
        let subCategory = null
        if (subCategoryName != null && hasSubCategories(category)) {
          subCategory = category.subCategories.find(sc => sc.subCategoryName == subCategoryName)
        }
        const subCategoryLayers = subCategory?.layers || []
        const layer = subCategoryLayers.find(l => l.layerName === layerName)

        if (layer) {
          layer.control = {
            enabled: status
          }
          layers.push({
            ...layer,
            categoryName,
            stateName,
            subCategoryName
          })
          compositeKeys.push(`${stateName}|${categoryName}|${subCategoryName}|${layerName}`)
        }
      })
      const updatedLayerKeys = updateMapLayers({
        all,
        layers,
        priorityMap,
        mapRef,
        status
      })

      if (updatedLayerKeys.length > 0) {
        yield put({
          type: 'setLayersByType',
          payload: {
            eventLayers: cloneDeep(layersByEvent),
            stateLayers: cloneDeep(layersByState)
          }
        })
        yield put({
          type: 'setLayersState',
          payload: { all: payload.all, enabled: status, keys: updatedLayerKeys }
        })
      }
    }
  },
  reducers: {
    setOverlayEnabled(state, { payload }) {
      const { id, enabled } = payload
      const overlays = [...state.overlays]

      const overlayIdx = overlays.findIndex(overlay => overlay.id == id)
      if (overlayIdx > -1) {
        overlays[overlayIdx].enabled = enabled
      }

      return {
        ...state,
        overlays
      }
    },
    setLayersLoading(state, { payload }) {
      return {
        ...state,
        [payload.type + 'LayersLoading']: payload.status
      }
    },
    setStates(state, { payload: states }) {
      return {
        ...state,
        states
      }
    },
    setLayersByType(state, { payload: { eventLayers, stateLayers } }) {
      return {
        ...state,
        layersByEvent: eventLayers,
        layersByState: stateLayers
      }
    },
    toggleLayerDrawer(state, { payload: stateName }) {
      const eventLayers = cloneDeep(state.layersByEvent)
      const stateLayers = cloneDeep(state.layersByState)
      const setToggleStatus = layers => {
        return layers.map(layer => {
          if (layer.stateName == stateName) {
            return {
              ...layer,
              isOpen: layer.isOpen != null ? !layer.isOpen : true
            }
          } else {
            return layer
          }
        })
      }
      return {
        ...state,
        layersByEvent: setToggleStatus(eventLayers),
        layersByState: setToggleStatus(stateLayers)
      }
    },
    setSelectedLayerCategory(state, { payload }) {
      return {
        ...state,
        selected: payload
      }
    },
    setLayersAtLocation(state, { payload: layersAtLocationData }) {
      return {
        ...state,
        layersAtLocationData
      }
    },
    setLayersVersion(state, { payload: version }) {
      return {
        ...state,
        layersVersion: version
      }
    },
    setLayersData(state, { payload: { eventLayers, stateLayers } }) {
      return {
        ...state,
        layersByEvent: eventLayers || state.layersByEvent,
        layersByState: stateLayers || state.layersByState
      }
    },
    clearRecentLayersData(state) {
      state.preferences.recent = []
      const data = {
        jobs: [
          {
            action: 'clear',
            path: 'recents/layers'
          }
        ]
      }
      saveLayers(data)
      return {
        ...state
      }
    },
    initLiveLayers(state, { payload: overlayLayers }) {
      if (overlayLayers == null) return state

      const overlays = overlayLayers.map((ll, i) => {
        return {
          ...ll,
          id: `${MAP_LAYER_IDS.overlay}-${ll.name.replace(/ /g, '_')}-${i}`,
          enabled: 0,
          priority: ll.priority || i + 1
        }
      })

      return {
        ...state,
        overlays
      }
    },
    setAllPreferences(state, { payload }) {
      return {
        ...state,
        preferences: {
          ...payload
        }
      }
    },
    setFavoriteStates(state, { payload }) {
      const { favorites } = state.preferences
      const data = { jobs: [] }
      if (favorites.states.find(s => payload.states.includes(s))) {
        data.jobs.push({ action: 'remove', path: 'favorites/states', values: [...payload.states] })
        favorites.states = favorites.states.filter(s => !payload.states.includes(s))
      } else {
        data.jobs.push({ action: 'add', path: 'favorites/states', values: [...payload.states] })
        favorites.states = [...payload.states, ...favorites.states]
      }
      if (data.jobs.length) {
        saveFavoriteStates(data)
      }
      return {
        ...state,
        preferences: {
          ...state.preferences,
          favorites
        }
      }
    },
    setLayersOpacity(state, { payload }) {
      const {
        layersByEvent,
        layersByState,
        preferences: { category }
      } = state
      const compositeKey = `${payload.stateName}-${payload.categoryName}`
      const allLayers = [...layersByEvent, ...layersByState]
      const { categories } = allLayers.find(l => l.stateName == payload.stateName)
      const categoryInfo = categories.find(c => c.categoryName == payload.categoryName)
      categoryInfo.opacity = payload.opacity
      const data = { jobs: [] }
      const params = {
        path: 'global/layer/opacity',
        values: [{ [compositeKey]: payload.opacity }]
      }

      if (category.opacity[compositeKey]) {
        if (payload.opacity === DEFAULT_LAYER_OPACITY) {
          data.jobs.push({ action: 'remove', ...params })
          delete category.opacity[compositeKey]
        } else if (category.opacity[compositeKey] !== payload.opacity) {
          data.jobs.push({ action: 'add', ...params })
          category.opacity[compositeKey] = payload.opacity
        }
      } else {
        data.jobs.push({ action: 'add', ...params })
        category.opacity[compositeKey] = payload.opacity
      }
      if (data.jobs.length) {
        saveCategoryOpacity(data)
      }
      return {
        ...state,
        layersByEvent: cloneDeep(layersByEvent),
        layersByState: cloneDeep(layersByState),
        preferences: {
          ...state.preferences,
          category
        }
      }
    },
    setLayersState(state, { payload }) {
      const { preferences } = state
      const data = { jobs: [] }
      if (payload.enabled) {
        if (payload.all) {
          data.jobs.push({ action: 'add', path: 'layers/visible', values: [...payload.keys] }, { action: 'clear', path: 'recents/layers' })
          preferences.active = payload.keys
          preferences.recent = []
        } else {
          data.jobs.push(
            { action: 'add', path: 'layers/visible', values: [...payload.keys] },
            { action: 'remove', path: 'recents/layers', values: [...payload.keys] }
          )
          preferences.active = [...payload.keys, ...preferences.active]
          preferences.recent = preferences.recent.filter(r => !payload.keys.includes(r))
        }
      } else {
        if (payload.all) {
          data.jobs.push({ action: 'add', path: 'recents/layers', values: [...payload.keys] }, { action: 'clear', path: 'layers/visible' })
          preferences.active = []
          preferences.recent = [...payload.keys, ...preferences.recent]
        } else {
          data.jobs.push(
            { action: 'add', path: 'recents/layers', values: [...payload.keys] },
            { action: 'remove', path: 'layers/visible', values: [...payload.keys] }
          )
          preferences.active = preferences.active.filter(r => !payload.keys.includes(r))
          preferences.recent = [...payload.keys, ...preferences.recent]
        }
      }
      preferences.active = preferences.active.slice(0, MAX_ACTIVE_LAYERS)
      preferences.recent = preferences.recent.slice(0, MAX_RECENT_LAYERS)

      if (data.jobs.length) {
        saveLayers(data)
      }
      return {
        ...state,
        preferences
      }
    },
    setLayerPriorityMap(state, { payload }) {
      return {
        ...state,
        layerPriorityMap: payload
      }
    }
  }
}
